import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { GradientCard, GradientVariant } from '../ui/GradientCard';

interface Metric {
    id?: string;
    label: string;
    value: string | number;
    subtext: string;
    icon: LucideIcon;
    variant: GradientVariant;
}

interface StatGridProps {
    metrics: Metric[];
    isDarkMode: boolean;
    onCardClick?: (metricId: string) => void;
}

export function StatGrid({ metrics, isDarkMode, onCardClick }: StatGridProps) {
    const getIconColor = (variant: GradientVariant, isDark: boolean) => {
        if (isDark) return 'text-white';
        switch (variant) {
            case 'primary': return 'text-blue-600';
            case 'action': return 'text-rose-600';
            case 'warning': return 'text-orange-600';
            case 'success': return 'text-emerald-600';
            case 'entity': return 'text-purple-600';
            case 'critical': return 'text-red-600';
            case 'inactive': return 'text-gray-600';
            default: return 'text-gray-900';
        }
    };

    return (
        <div className="grid grid-cols-2 gap-3 mb-6">
            {metrics.map((metric, index) => (
                <motion.div
                    key={metric.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, type: "spring" }}
                    whileHover={{ scale: 1.05, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    className="h-full"
                >
                    <GradientCard
                        variant={metric.variant}
                        isDarkMode={isDarkMode}
                        onClick={() => onCardClick && metric.id && onCardClick(metric.id)}
                        className="h-full aspect-square rounded-3xl"
                        innerClassName="flex flex-col justify-center items-center text-center h-full rounded-[1.3rem]"
                    >
                        <motion.div
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.6 }}
                        >
                            <metric.icon
                                className={`w-10 h-10 mx-auto mb-3 ${getIconColor(metric.variant, isDarkMode)}`}
                                strokeWidth={2.5}
                            />
                        </motion.div>
                        <motion.div
                            className={`text-5xl mb-2 font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                            initial={{ scale: 1.3, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                        >
                            {metric.value}
                        </motion.div>
                        <div className={`text-sm mb-1 font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'}`}>{metric.label}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-white/70' : 'text-gray-500'}`}>{metric.subtext}</div>
                    </GradientCard>
                </motion.div>
            ))}
        </div>
    );
}
